import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { map } from 'rxjs/operators';

import { Group, GroupInput, Location, PagedResult, Permission, PermissionCategorie, Segment, User, } from '@core/interfaces';

@Injectable({
  providedIn: 'root'
})
export class GroupService {
  constructor(
    private readonly apollo: Apollo
  ) {}

  public async loadData({ where, options }): Promise<PagedResult<Group>> {
    return this.apollo.query({
      query: gql`
        query listGroups($where: GroupWhereInput, $orderBy: GroupOrderByInput, $limit: Int, $offset: Int) {
          groups(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
            totalCount
            items {
              id
              name
              description
              createdAt
              archived
              type
            }
          }
        }
      `,
      variables: {
        orderBy: options.orderBy,
        limit: options.limit,
        offset: options.offset,
        where: where
      }
    })
      .toPromise()
      .then((response) => response.data['groups'] as PagedResult<Group>)
      .then((data) => {
        return {
          items: data.items,
          totalCount: data.totalCount,
          filtered: Boolean(!!where.name_contains)
        };
      });
  }

  public async save(groupId: string, groupInput: GroupInput): Promise<Group> {
    if (groupId) {
      return this.updateGroup(groupId, groupInput);
    }

    return this.createGroup(groupInput);
  }

  public async getGroupById(id: string): Promise<Group | null> {
    return this.apollo
      .query({
        query: gql`
          query getGroupById($id: ID!) {
            group(id: $id) {
              id
              name
              description
              createdAt
              type
              locked
              archived
              default
              users {
                id
                name
                email
                archived
                avatarUrl
              }
              permissions {
                id
                name
                category
              }
            }
          }
        `,
        variables: {
          id
        }
      })
      .toPromise()
      .then((response) => response.data['group'] as Group);
  }

  public async updateGroup(id: string, input: GroupInput): Promise<Group> {
    return this.apollo
      .query({
        query: gql`
          mutation updateGroup($id: ID!, $input: GroupInput!) {
            updateGroup(id: $id, input: $input) {
              id
              name
              description
              createdAt
              type
              locked
              default
              users {
                id
                name
                email
                archived
                avatarUrl
              }
              permissions {
                id
                name
                category
              }
            }
          }
        `,
        variables: {
          id,
          input
        }
      })
      .pipe(map((response) => response.data['updateGroup'] as Group))
      .toPromise();
  }

  public async createGroup(input: GroupInput): Promise<Group> {
    return this.apollo
      .query({
        query: gql`
          mutation createGroup($input: GroupInput!) {
            createGroup(input: $input) {
              id
              name
              description
              createdAt
              type
              locked
              default
              users {
                id
                name
                email
                archived
                avatarUrl
              }
              permissions {
                id
                name
                category
              }
            }
          }
        `,
        variables: {
          input
        }
      })
      .pipe(map((response) => response.data['createGroup'] as Group))
      .toPromise();
  }

  private async archiveGroup(groupId: string): Promise<Group> {
    return this.apollo
      .query({
        query: gql`
          mutation archiveGroup($groupId: ID!) {
            archiveGroup(id: $groupId) {
              id
              archived
            }
          }
        `,
        variables: {
          groupId
        }
      })
      .pipe(map((response) => response.data['archiveGroup'] as Group))
      .toPromise();
  }

  private async unarchiveGroup(groupId: string): Promise<Group> {
    return this.apollo
      .query({
        query: gql`
          mutation unarchiveGroup($groupId: ID!) {
            unarchiveGroup(id: $groupId) {
              id
              archived
            }
          }
        `,
        variables: {
          groupId
        }
      })
      .pipe(map((response) => response.data['unarchiveGroup'] as Group))
      .toPromise();
  }

  public archiveOrUnarchiveUserGroup({ id, archived }: Partial<Group>): Promise<Group> {
    return archived
      ? this.unarchiveGroup(id)
      : this.archiveGroup(id);
  }

  public async listAllUsers({ where, limit, offset, groupId }): Promise<PagedResult<User>> {
    return this.apollo
      .query({
        query: gql`
          query users($where: UserWhereInput, $limit: Int, $offset:Int, $groupId: ID) {
            users(where: $where, orderBy: name_asc, limit: $limit, offset: $offset) {
              totalCount
              pageInfo {
                hasNextPage
                hasPreviousPage
              }
              items {
                id
                name
                email
                avatarUrl
                groupsConnection(where: { id: $groupId }, limit: 1, offset: 0) {
                  items {
                    id
                    name
                  }
                }
              }
            }
          }`,
        variables: {
          where,
          limit,
          offset,
          groupId
        }
      })
      .toPromise()
      .then((response) => response.data['users'] as PagedResult<User>);
  }

  public async listUsers({ where, limit, offset}: { where: any, limit: number, offset: number, groupId?: string }): Promise<PagedResult<User>> {
    return this.apollo
      .query({
        query: gql`
          query listUsers($where: UserWhereInput, $limit: Int, $offset:Int) {
            users(where: $where, orderBy: name_asc, limit: $limit, offset: $offset) {
              totalCount
              items {
                id
                name
                email
                archived
                avatarUrl
              }
            }
          }`,
        variables: {
          where,
          limit,
          offset
        }
      })
      .toPromise()
      .then((response) => response.data['users'] as PagedResult<User>);
  }

  public async listLocations({ where, groupId, limit, offset }: { where: any, groupId?: string, limit: number, offset: number }): Promise<PagedResult<Location>> {
    return this.apollo
      .query({
        query: gql`
          query listLocations($where: LocationWhereInput, $groupId: ID $limit: Int, $offset:Int) {
            locations(where: $where, orderBy: { column: name, direction: asc }, limit: $limit, offset: $offset) {
              totalCount
              items {
                id
                name
                archived
                address {
                  formattedAddress
                }
                groupsConnection(where: { id: $groupId }, limit: 1, offset: 0){
                  items {
                    id
                    name
                    type
                  }
                }
              }
            }
          }`,
        variables: {
          where,
          groupId,
          limit,
          offset
        }
      })
      .toPromise()
      .then((response) => response.data['locations'] as PagedResult<Location>);
  }

  public async listSegments({ where, groupId, limit, offset }: { where?: any, groupId?: string, limit: number, offset: number }): Promise<PagedResult<Segment>> {
    return this.apollo
      .query({
        query: gql`
          query listSegments($where: SegmentWhereInput, $groupId: ID $limit: Int, $offset:Int) {
            segments(where: $where, orderBy: name_asc, limit: $limit, offset: $offset) {
              totalCount
              items {
                id
                name
                archived
                groupsConnection(where: { id: $groupId }, limit: 1, offset: 0){
                  items {
                    id
                    name
                    type
                  }
                }
              }
            }
          }`,
        variables: {
          where,
          groupId,
          limit,
          offset
        }
      })
      .toPromise()
      .then((response) => response.data['segments'] as PagedResult<Segment>);
  }

  public async unlinkUser(userId: string, groupId: string): Promise<void> {
    await this.apollo.mutate({
      mutation: gql`
        mutation removeUserFromGroup($input: RemoveUserFromGroupInput!) {
          removeUserFromGroup(input: $input) {
            group {
              id
              name
            }
            user {
              id
              name
            }
          }
        }
      `,
      variables: {
        input: {
          userId,
          groupId
        }
      }
    }).toPromise();
  }

  public async removeLocationFromGroup(items: Array<{ locationId: string, groupId: string }>): Promise<void> {
    await this.apollo.mutate({
      mutation: gql`
        mutation removeLocationFromGroup($input: RemoveLocationFromGroupInput!) {
          removeLocationFromGroup(input: $input) {
            group {
              id
            }
            location {
              id
            }
          }
        }
      `,
      variables: {
        input: {
          items
        }
      }
    }).toPromise();
  }

  public async removeSegmentFromGroup({ groupId, segmentIds }: { groupId: string, segmentIds: string[] }): Promise<Segment[]> {
    return this.apollo.mutate({
      mutation: gql`
        mutation removeSegmentFromGroup($input: RemoveSegmentFromGroupInput! $groupId: ID!) {
          removeSegmentFromGroup(input: $input) {
            group {
              id
            }
            segment {
              id
              groupsConnection(where: { id: $groupId }, limit: 1, offset: 0){
                items {
                  id
                  name
                  type
                }
              }
            }
          }
        }
      `,
      variables: {
        groupId: groupId,
        input: {
          items: segmentIds.map((segmentId) => ({
            groupId: groupId,
            segmentId: segmentId
          }))
        }
      }
    }).toPromise()
      .then((response: any) => response.data.removeSegmentFromGroup.map((item: { segment: Segment }) => item && item.segment));
  }

  public async addLocationToGroup(items: Array<{ locationId: string, groupId: string }>): Promise<void> {
    await this.apollo.mutate({
      mutation: gql`
        mutation addLocationToGroup($input: AddLocationToGroupInput!) {
          addLocationToGroup(input: $input) {
            group {
              id
            }
            location {
              id
              name
            }
          }
        }
      `,
      variables: {
        input: {
          items
        }
      }
    }).toPromise();
  }

  public async linkAllLocationsToGroup(input: { groupId: string }): Promise<void> {
    await this.apollo.mutate({
      mutation: gql`
        mutation linkAllLocationsToGroup($input: LinkAllLocationsToGroupInput!) {
          linkAllLocationsToGroup(input: $input) {
            id
            name
          }
        }
      `,
      variables: {
        input
      }
    }).toPromise();
  }

  public async linkUsersToGroup(items: {groupId: string, userId: string[] } ): Promise<void> {
    await this.apollo.mutate({
      mutation: gql`
        mutation AddUsersToGroups($input: AddUsersToGroupsInput!) {
          AddUsersToGroups(input: $input) {
            group {
              id
            }
            user {
              id
              name
            }
          }
        }
      `,
      variables: {
        input: {
          items: items.userId.map((userId) => ({
            groupId: items.groupId,
            userId: userId
          }))
        }
      }
    }).toPromise();
  }

  public async linkAllUsersToGroup(input: { groupId: string }): Promise<void> {
    await this.apollo.mutate({
      mutation: gql`
        mutation linkAllUsersToGroup($input: LinkAllUsersToGroupInput!) {
          linkAllUsersToGroup(input: $input) {
            id
            name
          }
        }
      `,
      variables: {
        input
      }
    }).toPromise();
  }

  public async addSegmentToGroup({ groupId, segmentIds }: { groupId: string, segmentIds: string[] }): Promise<Segment[]> {
    return this.apollo.mutate({
      mutation: gql`
        mutation addSegmentToGroup($input: AddSegmentToGroupInput! $groupId: ID!) {
          addSegmentToGroup(input: $input) {
            group {
              id
            }
            segment {
              id
              name
              archived
              groupsConnection(where: { id: $groupId }, limit: 1, offset: 0){
                items {
                  id
                  name
                  type
                }
              }
            }
          }
        }
      `,
      variables: {
        groupId: groupId,
        input: {
          items: segmentIds.map((segmentId) => ({
            groupId: groupId,
            segmentId: segmentId
          }))
        }
      }
    }).toPromise()
      .then((response: any) => response.data.addSegmentToGroup.map((item: { segment: Segment }) => item && item.segment));
  }

  public async listAllPermissions(): Promise<Permission[]> {
    return this.apollo.query({
      fetchPolicy: 'cache-first',
      query: gql`
        query permissions {
          permissions {
            id
            name
            category
            visible
            translation {
              en {
                title
                description
              }
              es {
                title
                description
              }
              pt {
                title
                description
              }
            }
          }
        }
      `,
    }).toPromise()
      .then((response) => response.data['permissions'] as Permission[]);
  }

  public async listAllPermissionsCategories(): Promise<PermissionCategorie[]> {
    return this.apollo.query({
      query: gql`
        query permissionsCategories {
          permissionsCategories {
            id
            category
            translation {
              pt {
                title
                description
              }
              en {
                title
                description
              }
              es {
                title
                description
              }
            }
            permissions {
              id
              name
              category
              featureFlag
              translation {
                pt {
                  title
                  description
                }
                en {
                  title
                  description
                }
                es {
                  title
                  description
                }
              }
            }
          }
        }
      `,
    }).toPromise()
      .then((response) => response.data['permissionsCategories'] as PermissionCategorie[]);
  }
}
